import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Loader2,
  Package,
  ChevronDown,
} from "lucide-react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "react-toastify/dist/ReactToastify.css";

const API = "https://nissi-india.com/api/billPassGst";

/* helpers */
const fmt2 = (n) => parseFloat(n || 0).toFixed(2);
const round2 = (n) =>
  Math.round((parseFloat(n || 0) + Number.EPSILON) * 100) / 100;

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB");
};

function computeTotals(items, charges) {
  let totIt = 0,
    subtotal = 0,
    sumDs = 0;
  let sumVat = 0,
    sumCst = 0,
    sumEd = 0;
  let calAmt = 0,
    calGst = 0;

  for (const item of items) {
    const price = round2(parseFloat(item.fld_poprice || 0));
    const qty = parseFloat(item.fld_acceptedqty || 0);
    const ds = parseFloat(item.fld_DS || 0);
    const vat = parseFloat(item.fld_VAT || 0);
    const cst = parseFloat(item.fld_CST || 0);
    const ed = parseFloat(item.fld_ED || 0);

    const total = round2(price * qty);
    const dsAmt = round2((total * ds) / 100);
    const distot = round2(total - dsAmt);

    const edAmt = round2((distot * ed) / 100);
    const vatAmt = round2((distot * vat) / 100);
    const cstAmt = round2((distot * cst) / 100);

    totIt += total;
    sumDs += dsAmt;
    subtotal += distot;
    sumVat += vatAmt;
    sumCst += cstAmt;
    sumEd += edAmt;
    calAmt += distot;
    calGst += vatAmt + cstAmt + edAmt;
  }

  const freight = parseFloat(charges.freight || 0);
  const insurance = parseFloat(charges.insurance || 0);
  const pf = parseFloat(charges.pf || 0);
  const other = parseFloat(charges.otherCharges || 0);
  const tcs = parseFloat(charges.tcsAmount || 0);

  const frSGST =
    charges.freightTax === "1" ? parseFloat(charges.freightSGST || 0) : 0;
  const frCGST =
    charges.freightTax === "1" ? parseFloat(charges.freightCGST || 0) : 0;
  const frIGST =
    charges.freightTax === "1" ? parseFloat(charges.freightIGST || 0) : 0;

  const inSGST =
    charges.insuranceTax === "1" ? parseFloat(charges.insuranceSGST || 0) : 0;
  const inCGST =
    charges.insuranceTax === "1" ? parseFloat(charges.insuranceCGST || 0) : 0;
  const inIGST =
    charges.insuranceTax === "1" ? parseFloat(charges.insuranceIGST || 0) : 0;

  const pfSGST = charges.pfTax === "1" ? parseFloat(charges.pfSGST || 0) : 0;
  const pfCGST = charges.pfTax === "1" ? parseFloat(charges.pfCGST || 0) : 0;
  const pfIGST = charges.pfTax === "1" ? parseFloat(charges.pfIGST || 0) : 0;

  const sg = round2(
    (frSGST * freight + inSGST * insurance + pfSGST * pf) / 100
  );
  const cg = round2(
    (frCGST * freight + inCGST * insurance + pfCGST * pf) / 100
  );
  const ig = round2(
    (frIGST * freight + inIGST * insurance + pfIGST * pf) / 100
  );

  const beforeRound = round2(
    calAmt +
      other +
      insurance +
      pf +
      freight +
      calGst +
      sg +
      cg +
      ig +
      tcs
  );

  const rem = round2(beforeRound % 1);
  let roundOff = 0;
  if (rem !== 0) {
    roundOff = rem >= 0 && rem < 0.5 ? -rem : round2(1 - rem);
  }

  const totalAmount = round2(beforeRound + roundOff);

  return {
    subTotal: round2(totIt),
    discount: round2(sumDs),
    afterDiscountTot: round2(subtotal),
    freight,
    insurance,
    pf,
    sgst: round2(sumVat + sg),
    cgst: round2(sumCst + cg),
    igst: round2(sumEd + ig),
    otherCharges: other,
    tcs,
    roundOff,
    totalAmount,
  };
}

/* Tally Header Autocomplete Cell */
/* Tally Header Select Cell */
function TallyHeaderCell({ value, isDark, onSelect, commonHeaders, baseColor }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Load all headers (no prefix = full list)
        const res = await axios.get(`${API}/bp_tallyHeaders`, {
          headers: commonHeaders,
        });
        setOptions(res.data.data || []);
      } catch {
        setOptions([]);
      }
      setLoading(false);
    };
    load();
  }, [commonHeaders]);

  return (
    <select
      value={value || ""}
      onChange={(e) => onSelect(e.target.value)}
      disabled={loading}
      className={`w-48 px-3 py-2 text-xs rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 ${
        isDark
          ? "bg-slate-800/60 border-slate-600 text-white"
          : "bg-white border-slate-300 text-slate-900"
      }`}
    >
      <option value="">
        {loading ? "Loading..." : "Select Tally Header"}
      </option>
      {options.map((opt) => (
        <option key={opt.fld_subgroupid} value={opt.fld_subgroupname}>
          {opt.fld_subgroupname}
          {opt.fld_groupname ? ` (${opt.fld_groupname})` : ""}
        </option>
      ))}
    </select>
  );
}
const BillPassingGstEntry = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const {
    selectedRows = [],
    supplierName = "",
    supplierId = "",
    billpassno = "",
    mode = "new",
  } = state || {};

  const isEditMode = mode === "edit" && !!billpassno;
  const firstRow = selectedRows[0] || {};

  /* session */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const storedBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";
  const loginUserId = localStorage.getItem("loginuserid") || userId || "37";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchId,
      "x-branch": storedBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchId, storedBranchType, userId]
  );

  const branchId =
    firstRow.branchId || firstRow.fld_Branchid || storedBranchId || 8;
  const branchType = firstRow.branchType || storedBranchType || "BRANCH";
  const grnConId = selectedRows
    .map((r) => r.fld_GRNConfid || r.grnConfirmId || "")
    .filter(Boolean)
    .join(",");

  const [viewMode] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  const [editBranchId, setEditBranchId] = useState(branchId);
  const [editSupplierId, setEditSupplierId] = useState("");
  const [editSupplierName, setEditSupplierName] = useState(supplierName);
  const [editGrnConfId, setEditGrnConfId] = useState("");
  const [editGrnConfDisplayNo, setEditGrnConfDisplayNo] = useState("");

  const [bpCode, setBpCode] = useState("");
  const [supplierAddress, setSupplierAddress] = useState("");
  const [refNo, setRefNo] = useState("");
  const [employees, setEmployees] = useState([]);
  const [poGrid, setPoGrid] = useState([]);
  const [itemDetails, setItemDetails] = useState([]);
  const [tallyHeaders, setTallyHeaders] = useState({});
  const [charges, setCharges] = useState({});
  const [grnIds, setGrnIds] = useState("");
  const [grnConfIds, setGrnConfIds] = useState("");
  const [calcData, setCalcData] = useState(null);
  const [rights, setRights] = useState({ hasAddRights: true });

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    refDate: "",
    refNo: "",
    remarks: "",
    employee: "",
  });

  const [loading, setLoading] = useState({
    init: false,
    poGrid: false,
    items: false,
    submit: false,
  });

  const setL = (key, val) => setLoading((p) => ({ ...p, [key]: val }));

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const readonlyClass = `w-full px-3 py-2.5 rounded-lg border text-sm cursor-not-allowed opacity-80 ${
    isDark
      ? "bg-slate-800 border-slate-700 text-slate-300"
      : "bg-gray-100 border-gray-300 text-gray-600"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const loadRights = async () => {
    try {
      const res = await axios.get(
        `${API}/bp_entryRights?loginuserid=${loginUserId}`,
        { headers: commonHeaders }
      );
      setRights(res.data);
      if (!res.data.hasRights) {
        toast.error("You don't have rights to access this page");
        navigate(-1);
      }
    } catch {}
  };

  const loadBpCode = async () => {
    try {
      const res = await axios.get(
        `${API}/bp_codegeneration?userid=${loginUserId}`,
        { headers: commonHeaders }
      );
      if (res.data.success) setBpCode(res.data.code);
    } catch (err) {
      toast.error("Failed to generate bill pass code");
    }
  };

  const loadSupplierAddress = async () => {
    if (!supplierId) return;
    try {
      const res = await axios.get(
        `${API}/bp_supplierAddress?supplierid=${supplierId}`,
        { headers: commonHeaders }
      );
      if (res.data.success) {
        setSupplierAddress(res.data.supplierAddress || "");
      }
    } catch {}
  };

  const loadRefNo = async () => {
    if (!grnConId) return;
    try {
      const res = await axios.get(
        `${API}/bp_addRefNo?grnconid=${encodeURIComponent(grnConId)}`,
        { headers: commonHeaders }
      );
      if (res.data.success) {
        setRefNo(res.data.refNo || "");
        setFormData((p) => ({ ...p, refNo: res.data.refNo || "" }));
      }
    } catch {}
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(
        `${API}/bp_employeeList?branchid=${branchId}`,
        { headers: commonHeaders }
      );
      setEmployees(res.data.data || []);
    } catch {}
  };

  const applyGRNConfFallback = useCallback(() => {
    if (!selectedRows.length) return;

    const poRows = selectedRows.map((r) => ({
      fld_POno: r.fld_GRNno || r.fld_GRNconfno || "-",
      fld_Date: r.fld_Date || "",
      fld_suppliername: supplierName || "-",
      fld_DueDate: r.fld_DCDate || "",
      fld_GRNid: String(r.fld_GRNConfid || ""),
      fld_purchaseorderid: String(r.fld_GRNConfid || ""),
      fld_GRNConfid: String(r.fld_GRNConfid || ""),
      _isFallback: true,
    }));
    setPoGrid(poRows);

    const confIds = selectedRows
      .map((r) => String(r.fld_GRNConfid || ""))
      .filter(Boolean)
      .join(",");

    setGrnIds(confIds);
    setGrnConfIds(confIds);

    const itemRows = selectedRows.map((r, i) => ({
      fld_itemid: String(r.fld_GRNConfid || i + 1),
      fld_itemcategory_name: r.fld_branchname || "-",
      fld_ItemName: r.fld_GRNno || r.fld_GRNconfno || "GRN Confirm Item",
      fld_itemHSNNo: "-",
      fld_uomname: "-",
      fld_POqty: 0,
      fld_grnqty: 0,
      fld_acceptedqty: 0,
      fld_poprice: "0.00",
      fld_DS: 0,
      fld_CST: 0,
      fld_VAT: 0,
      fld_ED: 0,
      fld_value: "0.00",
      fld_subgroupname: "",
      _isFallback: true,
      fld_GRNConfid: String(r.fld_GRNConfid || ""),
      fld_Invno: r.fld_Invno || "",
      fld_InvDate: r.fld_InvDate || "",
      fld_GRNconfno: r.fld_GRNconfno || "",
    }));

    setItemDetails(itemRows);
    const headers = {};
    itemRows.forEach((it) => {
      headers[it.fld_itemid] = "";
    });
    setTallyHeaders(headers);

    setCharges({
      freight: "0",
      insurance: "0",
      pf: "0",
      otherCharges: "0",
      transport: "0",
      tcsPercent: "0",
      tcsAmount: "0",
      freightTax: "0",
      insuranceTax: "0",
      pfTax: "0",
    });
  }, [selectedRows, supplierName]);

 const loadItems = useCallback(
  async (ponos) => {
    setL("items", true);
    try {
      // Load items first (required)
      const itemsRes = await axios.post(
        `${API}/bp_poItems`,
        { pur_ids: ponos },
        { headers: commonHeaders }
      );

      if (itemsRes.data.success && (itemsRes.data.items || []).length > 0) {
        const items = itemsRes.data.items;
        setItemDetails(items);
        const initHeaders = {};
        items.forEach((it) => {
          if (it.fld_itemid)
            initHeaders[it.fld_itemid] = it.fld_subgroupname || "";
        });
        setTallyHeaders(initHeaders);
      } else {
        applyGRNConfFallback();
        setL("items", false);
        return;
      }

      // Load charges separately (optional – don't kill items if it fails)
      try {
        const chargesRes = await axios.post(
          `${API}/bp_poCharges`,
          { ponos },
          { headers: commonHeaders }
        );
        if (chargesRes.data.success && chargesRes.data.data) {
          setCharges(chargesRes.data.data);
        }
      } catch (chargesErr) {
        console.warn("bp_poCharges failed, using defaults:", chargesErr.message);
        setCharges({
          freight: "0",
          insurance: "0",
          pf: "0",
          otherCharges: "0",
          transport: "0",
          tcsPercent: "0",
          tcsAmount: "0",
          freightTax: "0",
          insuranceTax: "0",
          pfTax: "0",
        });
      }
    } catch {
      applyGRNConfFallback();
    }
    setL("items", false);
  },
  [applyGRNConfFallback, commonHeaders]
);

  const loadPoGrid = useCallback(async () => {
    if (!grnConId) {
      applyGRNConfFallback();
      return;
    }

    setL("poGrid", true);
    try {
      const res = await axios.post(
        `${API}/bp_pogrid`,
        {
          branchid: String(branchId),
          supplier: String(supplierId || "%"),
          grninsconfirmno: grnConId,
        },
        { headers: commonHeaders }
      );

      if (res.data.success && (res.data.data || []).length > 0) {
        setPoGrid(res.data.data);
        const ids = res.data.grnIds || "";
        const confIds = res.data.grnConfIds || "";
        setGrnIds(ids);
        setGrnConfIds(confIds);
        if (ids) await loadItems(ids);
      } else {
        applyGRNConfFallback();
      }
    } catch {
      applyGRNConfFallback();
    }
    setL("poGrid", false);
  }, [
    grnConId,
    applyGRNConfFallback,
    branchId,
    supplierId,
    commonHeaders,
    loadItems,
  ]);

  const loadExistingBill = async () => {
    setL("poGrid", true);
    try {
      const headerRes = await axios.get(
        `${API}/bp_existingBill?billpassno=${encodeURIComponent(
          billpassno
        )}&branchid=${storedBranchId || branchId}`,
        { headers: commonHeaders }
      );

      if (!headerRes.data.success) {
        toast.error("Bill not found: " + billpassno);
        return;
      }

      const hdr = headerRes.data.data;
      setBpCode(hdr.billPassNo || billpassno);
      setEditGrnConfId(hdr.grnConfirmId || "");
      setEditSupplierId(hdr.supplierId || "");
      setEditBranchId(storedBranchId || branchId);

      const parseDateToISO = (raw) => {
        if (!raw) return "";
        const parts = raw.split("/");
        if (parts.length === 3) {
          const [d, m, y] = parts;
          const dt = new Date(
            `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
          );
          return isNaN(dt) ? "" : dt.toISOString().split("T")[0];
        }
        const dt = new Date(raw);
        return isNaN(dt) ? "" : dt.toISOString().split("T")[0];
      };

      const billDate = parseDateToISO(hdr.date);
      const refDate = parseDateToISO(hdr.refDate);
      const finalRef = refDate === "1900-01-01" ? "" : refDate;

      setFormData({
        date: billDate,
        refDate: finalRef,
        refNo: hdr.refNo || "",
        remarks: hdr.remarks || "",
        employee: "",
      });

      if (hdr.supplierId) {
        try {
          const supRes = await axios.get(
            `${API}/bp_supplierDetail?supplierid=${hdr.supplierId}`,
            { headers: commonHeaders }
          );
          if (supRes.data.success) {
            setEditSupplierName(supRes.data.data.supplierName || "");
            setSupplierAddress(supRes.data.data.address || "");
          }
        } catch {}
      }

      if (hdr.grnConfirmId) {
        try {
          const confRes = await axios.get(
            `${API}/bp_grnConfirmDetail?grnconfid=${encodeURIComponent(
              hdr.grnConfirmId
            )}`,
            { headers: commonHeaders }
          );
          if (confRes.data.success) {
            setEditGrnConfDisplayNo(
              confRes.data.displayNo || hdr.grnConfirmId
            );
          }
        } catch {}
      }

      const poRes = await axios.post(
        `${API}/bp_pogrid_existing`,
        {
          branchid: String(storedBranchId || branchId),
          supplier: String(hdr.supplierId || "%"),
          grnconfid: hdr.grnConfirmId || "",
        },
        { headers: commonHeaders }
      );

      let grnIdStr = "";
      if (poRes.data.success && poRes.data.data?.length > 0) {
        setPoGrid(poRes.data.data);
        grnIdStr = poRes.data.grnIds || "";
        setGrnIds(grnIdStr);
        setGrnConfIds(poRes.data.grnConfIds || "");
      }

      if (grnIdStr) await loadItems(grnIdStr);
    } catch (err) {
      toast.error("Failed to load bill");
    }
    setL("poGrid", false);
  };

  useEffect(() => {
    const init = async () => {
      setL("init", true);
      await loadRights();
      await loadEmployees();

      if (isEditMode) {
        await loadExistingBill();
      } else {
        await Promise.all([loadBpCode(), loadSupplierAddress(), loadRefNo()]);
        await loadPoGrid();
      }

      setL("init", false);
    };

    init();
  }, []);

  useEffect(() => {
    if (itemDetails.length > 0) {
      setCalcData(computeTotals(itemDetails, charges));
    } else {
      setCalcData(null);
    }
  }, [itemDetails, charges]);

  const handlePassBill = async () => {
    if (!formData.date) {
      toast.error("Please select a Date");
      return;
    }

    for (const item of itemDetails) {
      const id = item.fld_itemid;
      if (!tallyHeaders[id]) {
        toast.error(
          `Please add Tally BP Header for item: ${
            item.fld_ItemName || item.fld_itemname || id
          }`
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      const purchaseOrderGrid = poGrid.map((row) => ({
        hfgrnid: String(
          row.fld_GRNid || row.fld_grnid || row.fld_GRNConfid || ""
        ),
        hfpurid: String(row.fld_purchaseorderid || row.fld_GRNConfid || ""),
        hfgrnconid: String(row.fld_GRNConfid || row.fld_grnconfid || ""),
      }));

      const resolvedGrnConId =
        grnConId ||
        selectedRows
          .map((r) => String(r.fld_GRNConfid || ""))
          .filter(Boolean)
          .join(",");

      const resolvedGrnConfIds =
        grnConfIds ||
        selectedRows
          .map((r) => String(r.fld_GRNConfid || ""))
          .filter(Boolean)
          .join(",");

      const invoiceData = selectedRows
        .filter((r) => r.fld_Invno)
        .map((r) => ({
          GRNConid: String(r.fld_GRNConfid || ""),
          InvoiceNo: String(r.fld_Invno || ""),
          InvoiceDate: String(r.fld_InvDate || r.fld_Date || ""),
        }));

      const itemTallyHeadersArr = Object.entries(tallyHeaders)
        .filter(([, v]) => v)
        .map(([itemId, bpHeaderName]) => ({ itemId, bpHeaderName }));

      const payload = {
        branchId: String(branchId),
        supplierId: String(isEditMode ? editSupplierId : supplierId),
        grnConId: resolvedGrnConId,
        grnConfIds: resolvedGrnConfIds,
        dateText: formData.date,
        refDateText: formData.refDate || "",
        refNo: formData.refNo || refNo,
        remarks: formData.remarks,
        loginUserId,
        employeeId: formData.employee || null,
        purchaseOrderGrid,
        invoiceData,
        itemTallyHeaders: itemTallyHeadersArr,
        branchType,
      };

      const res = await axios.post(`${API}/bp_passbill`, payload, {
        headers: commonHeaders,
      });

      if (res.data.success) {
        toast.success(`Bill passed! Code: ${res.data.billPassCode || bpCode}`);
        setTimeout(() => navigate(-1), 1500);
      } else {
        throw new Error(res.data.message || "Failed to pass bill");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to pass bill"
      );
    }
    setSubmitting(false);
  };

  const handleInput = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const isLoading = loading.init || loading.poGrid || loading.items;

  const cardClass = isDark
    ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
    : `bg-white ${currentSkin.glass} text-black border-gray-200`;

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Bill Passing GST Entry</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Page Header Card */}
        <div className={`p-6 rounded-2xl shadow-xl border ${cardClass}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Bill Passing GST Entry</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {bpCode ? (
                    <>
                      Bill Pass No:{" "}
                      <span
                        className={`font-semibold text-${baseColor}-${baseShade}`}
                      >
                        {bpCode}
                      </span>
                    </>
                  ) : (
                    "Manage bill passing and GST details"
                  )}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>

          {isLoading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading data...
            </div>
          )}
        </div>

        {/* Bill Details Card */}
        <div className={`p-6 rounded-2xl shadow-xl border ${cardClass}`}>
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
            ></div>
            <h3 className="text-xl font-bold">Bill Details</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div>
              <label className={labelClass}>Bill Pass No</label>
              <input
                type="text"
                value={bpCode}
                readOnly
                className={readonlyClass}
              />
            </div>

            <div>
              <label className={labelClass}>GRN Confirm No</label>
              <input
                type="text"
                value={
                  isEditMode
                    ? editGrnConfDisplayNo
                    : selectedRows
                        .map((r) => r.fld_GRNconfno || r.grnConfirmNo || "")
                        .filter(Boolean)
                        .join(", ")
                }
                readOnly
                className={readonlyClass}
              />
            </div>

            <div>
              <label className={labelClass}>Supplier</label>
              <input
                type="text"
                value={isEditMode ? editSupplierName : supplierName}
                readOnly
                className={readonlyClass}
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className={labelClass}>Address</label>
              <input
                type="text"
                value={supplierAddress}
                readOnly
                className={readonlyClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInput("date", e.target.value)}
                disabled={viewMode}
                className={viewMode ? readonlyClass : inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Ref Date</label>
              <input
                type="date"
                value={formData.refDate}
                onChange={(e) => handleInput("refDate", e.target.value)}
                disabled={viewMode}
                className={viewMode ? readonlyClass : inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Ref No</label>
              <input
                type="text"
                value={formData.refNo || refNo}
                readOnly
                className={readonlyClass}
              />
            </div>

            <div>
              <label className={labelClass}>Employee</label>
              <select
                value={formData.employee}
                onChange={(e) => handleInput("employee", e.target.value)}
                disabled={viewMode}
                className={viewMode ? readonlyClass : inputClass}
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp.fld_employeeid} value={emp.fld_employeeid}>
                    {emp.fld_employee || emp.fld_Employee}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleInput("remarks", e.target.value)}
                disabled={viewMode}
                rows={2}
                className={viewMode ? readonlyClass : inputClass}
                placeholder="Optional remarks..."
              />
            </div>
          </div>
        </div>

        {/* Purchase Orders Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${cardClass}`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Purchase Orders</h3>
            </div>
            {loading.poGrid && (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  {["S.No", "PO No", "Date", "Supplier", "Due Date"].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-bold uppercase"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {loading.poGrid ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(5)].map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton
                            height={20}
                            baseColor={isDark ? "#374151" : "#f3f4f6"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : poGrid.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-12 text-gray-500"
                    >
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  poGrid.map((row, i) => (
                    <tr
                      key={i}
                      className={`transition ${
                        isDark
                          ? "hover:bg-gray-700/30"
                          : "hover:bg-gray-50"
                      } ${
                        row._isFallback
                          ? isDark
                            ? "bg-blue-900/10"
                            : "bg-blue-50"
                          : ""
                      }`}
                    >
                      <td className="px-4 py-3">{i + 1}</td>
                      <td className="px-4 py-3 font-medium">
                        {row.fld_POno || row.fld_pono || "-"}
                        {row._isFallback && (
                          <span
                            className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                              isDark
                                ? "bg-blue-800 text-blue-200"
                                : "bg-blue-100 text-blue-600"
                            }`}
                          >
                            GRN Confirm
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {fmtDate(row.fld_Date || row.fld_date) || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {row.fld_suppliername || supplierName || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {fmtDate(
                          row.fld_DueDate || row.fld_duedate || row.fld_DCDate
                        ) || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Details Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${cardClass}`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Item Details</h3>
            </div>
            {loading.items && (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1400px]">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  {[
                    "S.No",
                    "Category",
                    "Item Name",
                    "HSN No",
                    "UOM",
                    "Order Qty",
                    "GRN Qty",
                    "Accepted",
                    "Unit Price",
                    "DS%",
                    "CGST%",
                    "SGST%",
                    "IGST%",
                    "Total",
                    "Tally BP Header",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-3 text-left text-xs font-bold uppercase whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {loading.items ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(15)].map((__, j) => (
                        <td key={j} className="px-3 py-3">
                          <Skeleton
                            height={20}
                            baseColor={isDark ? "#374151" : "#f3f4f6"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : itemDetails.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Package size={48} className="text-gray-400" />
                        <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          No items found
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemDetails.map((item, i) => {
                    const itemId =
                      item.fld_itemid || item.fld_Itemid || String(i);
                    return (
                      <tr
                        key={itemId}
                        className={`transition ${
                          isDark
                            ? "hover:bg-gray-700/30"
                            : "hover:bg-gray-50"
                        } ${
                          item._isFallback
                            ? isDark
                              ? "bg-amber-900/10"
                              : "bg-amber-50"
                            : ""
                        }`}
                      >
                        <td className="px-3 py-3">{i + 1}</td>
                        <td className="px-3 py-3">
                          {item.fld_itemcategory_name || "-"}
                        </td>
                        <td className="px-3 py-3 font-medium">
                          {item.fld_ItemName || item.fld_itemname || "-"}
                          {item._isFallback && (
                            <div
                              className={`text-xs mt-0.5 ${
                                isDark ? "text-amber-400" : "text-amber-600"
                              }`}
                            >
                              GRN: {item.fld_GRNconfno}
                              {item.fld_Invno && ` · Inv: ${item.fld_Invno}`}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_itemHSNNo || "-"}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_uomname || "-"}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {item.fld_POqty || 0}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {item.fld_grnqty || 0}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {item.fld_acceptedqty || 0}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {fmt2(item.fld_poprice)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {item.fld_DS || 0}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {item.fld_CST || 0}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {item.fld_VAT || 0}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {item.fld_ED || 0}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-green-600">
                          {item.fld_value ||
                            fmt2(
                              (() => {
                                const p = parseFloat(item.fld_poprice || 0);
                                const q = parseFloat(
                                  item.fld_acceptedqty || 0
                                );
                                return p * q;
                              })()
                            )}
                        </td>
                        <td className="px-3 py-3">
                          {viewMode ? (
                            <span>{tallyHeaders[itemId] || "-"}</span>
                          ) : (
                            <TallyHeaderCell
                              value={tallyHeaders[itemId] || ""}
                              isDark={isDark}
                              baseColor={baseColor}
                              commonHeaders={commonHeaders}
                              onSelect={(name) =>
                                setTallyHeaders((p) => ({
                                  ...p,
                                  [itemId]: name,
                                }))
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Amount Summary Card */}
        {calcData && (
          <div className={`p-6 rounded-2xl shadow-xl border ${cardClass}`}>
            <div className="flex items-center gap-3 mb-6">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Amount Summary</h3>
            </div>

            <div className="max-w-md ml-auto space-y-2 text-sm">
              {[
                { label: "Sub Total", value: calcData.subTotal },
                { label: "Discount", value: calcData.discount },
                {
                  label: "After Discount Total",
                  value: calcData.afterDiscountTot,
                },
                { label: "Freight", value: calcData.freight },
                { label: "Insurance", value: calcData.insurance },
                { label: "P&F", value: calcData.pf },
                { label: "SGST", value: calcData.sgst },
                { label: "CGST", value: calcData.cgst },
                { label: "IGST", value: calcData.igst },
                { label: "Other Charges", value: calcData.otherCharges },
                { label: "TCS", value: calcData.tcs },
                { label: "Round Off", value: calcData.roundOff },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between">
                  <span className={isDark ? "text-gray-400" : "text-gray-500"}>
                    {label}
                  </span>
                  <span className="font-medium tabular-nums">
                    {fmt2(value)}
                  </span>
                </div>
              ))}

              <div
                className={`flex justify-between pt-3 border-t font-bold text-base ${
                  isDark
                    ? "border-gray-600 text-green-400"
                    : "border-gray-200 text-green-700"
                }`}
              >
                <span>Total Amount</span>
                <span className="tabular-nums">
                  {fmt2(calcData.totalAmount)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        {!viewMode && rights.hasAddRights && (
          <div className="flex justify-end pb-4">
            <button
              onClick={handlePassBill}
              disabled={submitting || itemDetails.length === 0 || isLoading}
              className={`inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-xl shadow-lg transition ${
                submitting || itemDetails.length === 0 || isLoading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Pass Bill
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme={isDark ? "dark" : "light"}
        toastStyle={{
          borderRadius: "12px",
          fontSize: "14px",
        }}
      />
    </LayoutWrapper>
  );
};

export default BillPassingGstEntry;